import * as yup from "yup";
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import LoadingButton from "@mui/lab/LoadingButton";

import useAxios from "../../../../hooks/useAxios";
import useAlert from "../../../../hooks/useAlert";
import {
  __getTmobilejCuratedGroups,
  __createTmobilejCuratedGroup,
  __updateTmobilejCuratedGroup,
  __deleteTmobilejCuratedGroup,
} from "../../../../api/sim";

const lineValidationSchema = yup.object({
  number: yup
    .string("number")
    .min(3, "Should be between 3 and 10 digits long")
    .max(10, "Should be between 3 and 10 digits long")
    .required(),
});

const validateNumbersText = (data) => {
  const errors = {};
  if (!data) return { errors, numbers: [] };

  const numbers = [];
  const lines = data.split("\n");
  lines.some((line, idx) => {
    const trimmed = String(line || "").trim();
    if (!trimmed) return false; // allow empty lines

    try {
      const obtainedData = { number: trimmed };
      lineValidationSchema.validateSync(obtainedData);
      if (isNaN(obtainedData.number)) throw new Error("Invalid Number");

      numbers.push(obtainedData.number);
      if (numbers.indexOf(obtainedData.number) !== numbers.length - 1) {
        errors.lines = `Duplicate number value in line ${idx + 1}`;
        return true;
      }
    } catch (err) {
      errors.lines = `Invalid format in line ${idx + 1}: ${line}`;
      return true;
    }

    return false;
  });

  return { errors, numbers };
};

const CuratedGroups = () => {
  const { setAlertMessage } = useAlert();
  const [groups, setGroups] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [remoteUuid, setRemoteUuid] = useState("");
  const [companyIdsText, setCompanyIdsText] = useState("");
  const [filterCompanyIdsText, setFilterCompanyIdsText] = useState("");
  const [numbersText, setNumbersText] = useState("");
  const [numbersError, setNumbersError] = useState("");

  const [groupsResp, fetchGroups, fetchLoading] = useAxios();
  const [createResp, createApi, createLoading] = useAxios();
  const [updateResp, updateApi, updateLoading] = useAxios();
  const [deleteResp, deleteApi, deleteLoading] = useAxios();

  const fetchGroupsWithFilter = () => {
    const companyIds = parseCompanyIds(filterCompanyIdsText);
    if (companyIds.length > 0) {
      fetchGroups({
        ...__getTmobilejCuratedGroups(),
        params: { company_id: companyIds },
      });
    } else {
      fetchGroups(__getTmobilejCuratedGroups());
    }
  };

  useEffect(() => {
    fetchGroupsWithFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (groupsResp?.status && groupsResp?.data) {
      setGroups(groupsResp.data);
    }
  }, [groupsResp]);

  useEffect(() => {
    if (!createResp?.status) return;
    setAlertMessage({ type: "success", message: "Group created" });
    fetchGroupsWithFilter();
    handleCloseDialog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createResp]);

  useEffect(() => {
    if (!updateResp?.status) return;
    setAlertMessage({ type: "success", message: "Group updated" });
    fetchGroupsWithFilter();
    handleCloseDialog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateResp]);

  useEffect(() => {
    if (!deleteResp?.status) return;
    setAlertMessage({ type: "success", message: "Group deleted" });
    fetchGroupsWithFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteResp]);

  
  const parseCompanyIds = (text) => {
    if (!text || !String(text).trim()) return [];
    return String(text)
      .split(",")
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !Number.isNaN(n));
  };

  const handleOpenDialog = (group = null) => {
    if (group) {
      setEditingGroup(group);
      setName(group.name);
      setDescription(group.description ?? "");
      setRemoteUuid(group.remote_uuid ?? "");
      setCompanyIdsText(
        (group.company_ids || []).length
          ? (group.company_ids || []).join(", ")
          : ""
      );
      const numbersStr = (group.numbers || []).join("\n");
      setNumbersText(numbersStr);
      const { errors } = validateNumbersText(numbersStr);
      setNumbersError(errors.lines || "");
    } else {
      setEditingGroup(null);
      setName("");
      setDescription("");
      setRemoteUuid("");
      setCompanyIdsText("");
      setNumbersText("");
      setNumbersError("");
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingGroup(null);
    setName("");
    setDescription("");
    setRemoteUuid("");
    setCompanyIdsText("");
    setNumbersText("");
    setNumbersError("");
  };

  const handleNumbersTextChange = (e) => {
    const value = e.target.value;
    setNumbersText(value);
    const { errors } = validateNumbersText(value);
    setNumbersError(errors.lines || "");
  };

  const handleSave = async () => {
    const { errors, numbers } = validateNumbersText(numbersText);
    
    if (errors.lines) {
      setNumbersError(errors.lines);
      setAlertMessage({ type: "error", message: errors.lines });
      return;
    }

    if (numbers.length === 0) {
      setNumbersError("At least one valid phone number or short code is required");
      setAlertMessage({
        type: "error",
        message: "At least one valid phone number or short code is required",
      });
      return;
    }

    const companyIds = parseCompanyIds(companyIdsText);

    const trimmedDescription = description.trim();
    const payload = {
      name,
      numbers,
    };
    if (trimmedDescription !== "") {
      payload.description = trimmedDescription;
    } else if (editingGroup) {
      payload.description = null;
    }
    const trimmedUuid = remoteUuid.trim();
    if (trimmedUuid !== "") {
      payload.remote_uuid = trimmedUuid;
    } else if (editingGroup) {
      payload.remote_uuid = null;
    }
    if (companyIds.length > 0) {
      payload.company_ids = companyIds;
    } else if (editingGroup) {
      payload.company_ids = null;
    }

    if (editingGroup) {
      await updateApi({
        ...__updateTmobilejCuratedGroup(editingGroup.id),
        data: payload,
      });
      return;
    }

    await createApi({
      ...__createTmobilejCuratedGroup(),
      data: payload,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this group?")) return;
    await deleteApi(__deleteTmobilejCuratedGroup(id));
  };

  return (
    <Box sx={{ mt: 4, width: "100%", maxWidth: 800 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Curated Filter Groups</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Group
        </Button>
      </Box>
      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Filter by company IDs (comma-separated)"
          value={filterCompanyIdsText}
          onChange={(e) => setFilterCompanyIdsText(e.target.value)}
          placeholder="e.g. 1, 2, 3"
          sx={{ minWidth: 280 }}
        />
        <Button
          variant="outlined"
          onClick={() => fetchGroupsWithFilter()}
          disabled={fetchLoading}
        >
          Apply filter
        </Button>
      </Box>

      {fetchLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <List>
          {groups &&
            groups.map((group) => (
              <Card key={group.id} sx={{ mb: 2 }}>
                <CardContent>
                  <ListItem disableGutters>
                    <ListItemText
                      primary={group.name}
                      secondary={
                        [
                          group.description || null,
                          `${(group.numbers || []).length} numbers`,
                          group.remote_uuid
                            ? `Remote UUID: ${group.remote_uuid}`
                            : null,
                          (group.company_ids || []).length
                            ? `Company IDs: ${(group.company_ids || []).join(", ")}`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleOpenDialog(group)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={() => handleDelete(group.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mt: 1 }}
                  >
                    {(group.numbers || []).slice(0, 10).join(", ")}
                    {(group.numbers || []).length > 10 && " ..."}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          {groups.length === 0 && (
            <Typography variant="body1" align="center" color="textSecondary">
              No curated groups found.
            </Typography>
          )}
        </List>
      )}

      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingGroup ? "Edit Group" : "Create Group"}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Group Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <TextField
              fullWidth
              label="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the group"
            />
            <TextField
              fullWidth
              label="Remote UUID (optional)"
              value={remoteUuid}
              onChange={(e) => setRemoteUuid(e.target.value)}
              placeholder="External system identifier"
            />
            <TextField
              fullWidth
              label="Company IDs (comma-separated numbers)"
              value={companyIdsText}
              onChange={(e) => setCompanyIdsText(e.target.value)}
              placeholder="e.g. 1, 2, 3"
            />
            <TextField
              fullWidth
              multiline
              rows={10}
              label="Numbers (one per line)"
              placeholder="1234567890"
              value={numbersText}
              onChange={handleNumbersTextChange}
              error={Boolean(numbersError)}
              helperText={numbersError || "Enter 10-digit numbers, one per line."}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <LoadingButton
            loading={createLoading || updateLoading || deleteLoading}
            variant="contained"
            onClick={handleSave}
            disabled={!name || !numbersText || Boolean(numbersError)}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CuratedGroups;
